import { Badge, SvgIconProps, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useMemo } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';


const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '48px',
    borderBottom: '2px solid transparent',
    color: theme.palette.darkSlate.main,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    transitionDuration: '240ms'
  },
  rootActive: {
    color: theme.palette.secondary.main,
    borderBottomColor: theme.palette.secondary.main,
  },
  icon: {
    fontSize: '28px',
    color: 'inherit',
  },
  tooltip: {
    margin: '3px 0',
    backgroundColor: theme.palette.grey[800],
    fontSize: '14px',
    lineHeight: '16px',
    paddingTop: '5px',
    paddingBottom: '3px',
  },
}));

type _NavigationItemProps = {
  title: string;
  icon: React.ComponentType<SvgIconProps>;
  iconActive: React.ComponentType<SvgIconProps>;
  badge?: number;
};

export type NavigationLinkProps = _NavigationItemProps & {
  path: string;
  exact?: boolean;
  asAnchor?: boolean;
};


export const NavigationLink: React.FC<NavigationLinkProps> = (props) => {
  const { classes, cx } = useStyles();
  const match = useRouteMatch({
    path: props.path,
    exact: props.exact,
  });

  const rootClassName = cx(classes.root, {
    [classes.rootActive]: !!match,
  });

  const CurrentIcon = match ? props.iconActive : props.icon;

  const [ open, setOpen ] = React.useState(false);

  const link = useMemo(() => {
    if (props.asAnchor) {
      return (
        <a
          href={props.path}
          target="_blank"
          rel="noopener noreferrer"
          className={rootClassName}
        >
          <CurrentIcon className={classes.icon} />
        </a>
      );
    }

    if (props.badge) {
      return (
        <Link className={rootClassName} to={props.path}>
          <Badge badgeContent={props.badge} color="info">
            <CurrentIcon className={classes.icon} />
          </Badge>
        </Link>
      );
    }

    return (
      <Link className={rootClassName} to={props.path}>
        <CurrentIcon className={classes.icon} />
      </Link>
    );
  }, [ CurrentIcon, classes.icon, props.asAnchor, props.path, rootClassName, props.badge ]);

  return (
    <Tooltip
      open={open}
      onOpen={() => !match && setOpen(true)}
      onClose={() => setOpen(false)}
      title={props.title}
      classes={{
        tooltipPlacementBottom: classes.tooltip
      }}
    >
      {link}
    </Tooltip>
  );
};


export type NavigationButtonProps = _NavigationItemProps & {
  onClick: () => void;
};


export const NavigationButton: React.FC<NavigationButtonProps> = (props) => {
  const { classes } = useStyles();
  const CurrentIcon = props.icon;

  const element = useMemo(() => {
    if (props.badge) {
      return (
        <div className={classes.root} onClick={props.onClick}>
          <Badge badgeContent={props.badge} color="error">
            <CurrentIcon className={classes.icon} />
          </Badge>
        </div>
      );
    }

    return (
      <div className={classes.root} onClick={props.onClick}>
        <CurrentIcon className={classes.icon} />
      </div>
    );
  }, [ CurrentIcon, classes.icon, classes.root, props.badge, props.onClick ]);

  return (
    <Tooltip
      title={props.title}
      classes={{
        tooltipPlacementBottom: classes.tooltip
      }}
    >
      {element}
    </Tooltip>
  );
};
